import React, { useState, useEffect } from "react";
import SEO from "../seo";
const RolesAndPermissions = () => {
  const [activeSection, setActiveSection] = useState("add-staff");

  useEffect(() => {
    const handleScroll = () => {
      const sections = [
        "add-staff",
        "staff-statuses",
        "accept-invitation",
        "deactivate-reactivate",
        "delete-role",
        "assign-remove-roles",
        "transfer-ownership",
      ];

      const scrollPosition = window.scrollY + 100;

      for (const sectionId of sections) {
        const element = document.getElementById(sectionId);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (
            scrollPosition >= offsetTop &&
            scrollPosition < offsetTop + offsetHeight
          ) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="flex flex-col lg:flex-row gap-6">
      <SEO
        title="Roles and Permissions | xPage Help Center"
        description="Learn how to manage staff roles and permissions in your xPage store. Add staff, assign roles, control access, deactivate/reactivate staff, and transfer store ownership efficiently."
        keywords="xPage, roles, permissions, add staff, assign roles, deactivate staff, reactivate staff, transfer store ownership"
        canonical="https://help.xpage.ai/help/landing-page-account/Roles%20and%20permissions"
        type="article"
      />
      {/* Main Content */}
      <div className="flex-1 prose max-w-none">
        {/* Header Section */}
        <header className="mb-6">
          <h1 className="text-systemTitle text-2xl font-semibold mb-3">
            Roles and permissions ( coming Soon )
          </h1>
          <p className="text-sm text-systemLabel">
            You can add staff to your xPage store by assigning roles, ensuring
            that each team member has the right level of access to perform their
            tasks.
          </p>
          <p className="text-sm text-systemLabel">
            With xPage's role and permission management system, you can control
            what actions staff can take based on their responsibilities. For
            example, you might give managers access to product and order
            management, while limiting other staff to customer service tasks.
            This structure keeps your store secure and operations efficient.
          </p>
        </header>

        {/* Add Staff Section */}
        <section id="add-staff" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Add staff
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps:
            </h3>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Log in to the xPage admin and navigate to Settings then Staff
                  Accounts.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Click Add Staff.</span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Enter the staff member's email address. They will receive an
                  invitation and store notifications at this email.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  In the Role section, assign at least one role.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  (Optional) Click View Role Permissions to review the assigned
                  role's access rights. You can edit an existing role or create
                  a new custom role if needed.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click Send Invite. Invitations are valid for 7 days; if not
                  accepted, they must be re-sent.
                </span>
              </li>
            </ol>
          </div>
        </section>

        {/* Staff Statuses Section */}
        <section id="staff-statuses" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Staff Statuses
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Once sent, the staff member will appear in the staff list with one
              of the following statuses:
            </p>

            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Status</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Description
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Joined</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    The invitation has been accepted.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Pending
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Invitation sent but not accepted yet.
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Expired
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Invitation expired after 7 days.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Declined
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Invitation was rejected.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        {/* Accept Invitation Section */}
        <section id="accept-invitation" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Accept an invitation
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Invited staff must accept within 7 days:
            </p>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Open the invitation email and click Accept Invitation.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Log in to your xPage account, or create one if you don't
                  already have one.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click Accept Invite to confirm.
                </span>
              </li>
            </ol>
          </div>
        </section>

        {/* Deactivate/Reactivate Section */}
        <section id="deactivate-reactivate" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Deactivate or reactivate staff accounts
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <p className="text-sm text-systemLabel mb-4">
              Store owners and staff with Staff Management permissions can
              control access to the xPage admin.
            </p>

            <table className="w-full mb-6">
              <tbody className="border divide-y divide-systemBorder">
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">Action</strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Description
                  </td>
                </tr>
                <tr className="">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Deactivate account
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Immediately revoke admin access. Deactivated accounts do not
                    count toward your staff limit.
                  </td>
                </tr>
                <tr className="bg-gray-400/10 transition-colors">
                  <td className="p-3 align-top w-1/3 border-r border-systemBorder">
                    <strong className="text-systemTitle text-sm">
                      Reactivate account
                    </strong>
                  </td>
                  <td className="p-3 align-top text-systemLabel text-sm">
                    Restore admin access with the previous settings or reset
                    them as needed.
                  </td>
                </tr>
              </tbody>
            </table>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Deactivate a staff account:
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4 mb-4">
              <li className="mb-2">
                <span className="font-medium">
                  Log in to xPage admin then Settings then Staff Accounts.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Find the staff member and click Deactivate.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Confirm by clicking Deactivate.
                </span>
              </li>
            </ol>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Reactivate a staff account:
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Log in to xPage admin then Settings then Staff Accounts.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Locate the deactivated staff member and click Reactivate.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Confirm by clicking Reactivate.
                </span>
              </li>
            </ol>
          </div>
        </section>

        {/* Delete Role Section */}
        <section id="delete-role" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Delete a role
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mb-4 rounded-r-md">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Note:</span> Only custom roles
                can be deleted; system roles cannot.
              </p>
            </div>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Steps to delete:
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Log in to xPage admin then Settings then Staff Accounts then
                  Manage Roles.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click … next to the role and select Delete.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Confirm deletion. Staff assigned to this role will lose the
                  associated permissions.
                </span>
              </li>
            </ol>
          </div>
        </section>

        {/* Assign/Remove Roles Section */}
        <section id="assign-remove-roles" className="mb-6 scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Assign or remove roles from staff
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Assign roles:
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4 mb-4">
              <li className="mb-2">
                <span className="font-medium">
                  Log in to xPage admin then Settings then Staff Accounts.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Select the staff member, click … then Edit.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  In the details section, select one or more roles to assign.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Click Save.</span>
              </li>
            </ol>

            <h3 className="text-sm font-semibold text-systemTitle mb-2">
              Remove roles:
            </h3>
            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4 mb-4">
              <li className="mb-2">
                <span className="font-medium">
                  Log in to xPage admin then Settings then Staff Accounts.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Select the staff member, click … then Edit.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Deselect the roles to remove.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">Click Save.</span>
              </li>
            </ol>

            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 rounded-r-md">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Note:</span> If multiple roles
                are assigned, the staff member will have the combined
                permissions of all roles.
              </p>
            </div>
          </div>
        </section>

        {/* Transfer Store Ownership Section */}
        <section id="transfer-ownership" className="scroll-mt-20">
          <h2 className="text-sm font-semibold text-systemTitle mb-1">
            Transfer store ownership
          </h2>

          <div className="bg-systemBackground border border-systemBorder rounded-lg p-4 mt-2">
            <div className="bg-system200 border-l-4 border-system400 px-4 py-3 mb-4 rounded-r-md">
              <p className="text-sm text-systemLabel">
                <span className="font-semibold">Note:</span> Only the current
                store owner can perform this action.
              </p>
            </div>

            <ol className="list-decimal list-inside space-y-1 text-sm text-systemLabel ml-4">
              <li className="mb-2">
                <span className="font-medium">
                  Log in to xPage admin then Settings then Staff Accounts.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Under the Store Owner section, click Transfer Ownership.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Enter the new owner's email address.
                </span>
              </li>
              <li className="mb-2">
                <span className="font-medium">
                  Click Transfer Ownership to confirm.
                </span>
              </li>
            </ol>
          </div>
        </section>
      </div>

      {/* Table of Contents Sidebar */}
      <div className="lg:w-64 flex-shrink-0 lg:block hidden">
        <div className="sticky top-6 bg-systemBackground border border-systemBorder rounded-lg p-4">
          <h3 className="text-systemTitle text-sm font-semibold mb-3">
            On this page
          </h3>
          <ul className="text-systemLabel text-sm space-y-2">
            <li>
              <a
                href="#add-staff"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "add-staff"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Add Staff
              </a>
            </li>
            <li>
              <a
                href="#staff-statuses"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "staff-statuses"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Staff Statuses
              </a>
            </li>
            <li>
              <a
                href="#accept-invitation"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "accept-invitation"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Accept Invitation
              </a>
            </li>
            <li>
              <a
                href="#deactivate-reactivate"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "deactivate-reactivate"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Deactivate/Reactivate
              </a>
            </li>
            <li>
              <a
                href="#delete-role"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "delete-role"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Delete Role
              </a>
            </li>
            <li>
              <a
                href="#assign-remove-roles"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "assign-remove-roles"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Assign/Remove Roles
              </a>
            </li>
            <li>
              <a
                href="#transfer-ownership"
                className={`block py-1 transition-colors border-l-2 pl-3 ${
                  activeSection === "transfer-ownership"
                    ? "text-deepPurple font-semibold border-deepPurple"
                    : "border-transparent hover:text-deepPurple"
                }`}
              >
                Transfer Ownership
              </a>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default RolesAndPermissions;
